import { useEffect, useRef, useState } from 'react'
import AOS from 'aos'
import { SITE,ROUTES } from '@/constants'
import Button from '@ui/Button'
import { useLocation } from 'react-router-dom'
import TransformBanner from '@sections/TransformBanner'
import Breadcrumb from '@common/Breadcrumb'
import { useInView } from '@helpers/useIntersectionObserver'
import MetaInfo from '@common/MetaInfo'
import { usePrefersReducedMotion } from '@helpers/usePrefersReducedMotion'
import { metaTitleFor } from '../assets/js/navBreadcrumb'
const { label, pageDesc, metaDesc } = metaTitleFor(ROUTES.CLIENT_CORNER)
// ── Logo grid images ──────────────────────────────────────────────────
const LOGO_IMAGES_GRID = [
  '/assets/images/client-logo/biguine_logo.jpg',
  '/assets/images/client-logo/dash_two_logo.jpg',
  '/assets/images/client-logo/dhl-kyc-logo.jpg',
  '/assets/images/client-logo/eesl_logo.jpg',
  '/assets/images/client-logo/nip_logo.png',
  '/assets/images/client-logo/pwc_logo.jpg',
  '/assets/images/client-logo/qualilifediagnostics.png',
  '/assets/images/client-logo/sneha_logo.jpg',
  '/assets/images/client-logo/sohum_logo.png',
  '/assets/images/client-logo/jjacademy.png',
  '/assets/images/client-logo/pfc_logo.png',
  '/assets/images/client-logo/posoco_final_logo.png',
]

const DIRECTIONS = ['top', 'bottom', 'left', 'right']

const LOGO_TILES = Array.from({ length: 25 }, (_, i) => ({
  id: i,
  image: LOGO_IMAGES_GRID[i % LOGO_IMAGES_GRID.length],
  dir: DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)],
}))

// ── Testimonials ──────────────────────────────────────────────────────
const TESTIMONIALS_PHOTOS = [
  '/assets/images/client-people/gauri-nigudkar-resonateconsulting.jpg',
  '/assets/images/client-people/dr-arbinder-singal-mediangels.jpg',
  '/assets/images/client-people/pawan-jain-aabhushanjewellers.jpg',
]

const TESTIMONIALS = [
  {
    name: 'Gauri Nigudkar',
    role: 'OD Consultant & Coach, Resonate Consulting',
    photo: TESTIMONIALS_PHOTOS[0],
    quote: `We have used the services of CruxBytes more than once. Vikiraj has been our one point contact and we have had no need to look further. We were at our wit's end as the vendors could not understand what we wanted. Vikiraj was a refreshing change as we found a partner in him and his team. He understood our work, captured our essence and delivered a job to everybody's satisfaction. A delight to work with!! Would recommend this team and its leader to one and all...You want quality and value for money? - Vikiraj is your man!!`,
  },
  {
    name: 'Arbinder Singal',
    role: 'Pediatric Urologist & Healthcare Entrepreneur, Angels Health Pvt. Ltd. (Mediangels.com)',
    photo: TESTIMONIALS_PHOTOS[1],
    quote: `Vikiraj is a hardworking and sincere individual with high inner motivation. He was willing to put in long hours and ensure availability whenever asked. We were very impressed with his absolute hands on approach and fast turnaround times.`,
  },
  {
    name: 'Pawan Jain',
    role: 'Proprietor, Aabhushan Jewellers',
    photo: TESTIMONIALS_PHOTOS[2],
    quote: `We got our website designed from CruxBytes and they did an excellent job by designing it to our expectations. Also they provide efficient hosting, maintenance and support. I hardly need to bother about any technicalities. They care of everything, every year! Good job.`,
  },
]

const TESTIMONIALS_AUTOPLAY_DELAY = 3000

// ── Logo grid component ───────────────────────────────────────────────
function LogoGrid() {
  const [flipped, setFlipped] = useState(new Set())
  // const reducedMotion =
  //   typeof window !== 'undefined' &&
  //   window.matchMedia('(prefers-reduced-motion: reduce)').matches
 const reducedMotion = usePrefersReducedMotion()
  useEffect(() => {
    if (reducedMotion) return
    const shuffle = arr => [...arr].sort(() => Math.random() - 0.5)
    let queue = shuffle(LOGO_TILES.map(t => t.id))
    let queueIdx = 0
    const getNext = () => {
      if (queueIdx >= queue.length) {
        queue = shuffle(LOGO_TILES.map(t => t.id))
        queueIdx = 0
      }
      return queue[queueIdx++]
    }
    const MAX_ACTIVE = 3
    const HOLD_MS = 1500
    const GAP_MS = 800
    let active = 0
    let stopped = false

    const startFlip = () => {
      if (stopped || active >= MAX_ACTIVE) return
      active++
      const id = getNext()
      setFlipped(prev => new Set([...prev, id]))
      setTimeout(() => {
        if (stopped) return
        setFlipped(prev => {
          const n = new Set(prev)
          n.delete(id)
          return n
        })
        setTimeout(() => {
          active--
          if (!stopped) startFlip()
        }, 500)
      }, HOLD_MS + 500)
    }

    const t1 = setTimeout(() => startFlip(), 400)
    const t2 = setTimeout(() => startFlip(), 400 + GAP_MS)
    const interval = setInterval(() => {
      if (active < MAX_ACTIVE) startFlip()
    }, GAP_MS)

    return () => {
      stopped = true
      clearTimeout(t1)
      clearTimeout(t2)
      clearInterval(interval)
    }
  }, [reducedMotion])

  return (
    <div className="logo-grid" aria-label="Our clients">
      {LOGO_TILES.map((tile,i) => (
        <div
          key={tile.id}
          className={`logo-tile logo-tile--${tile.dir}${flipped.has(tile.id) ? ' logo-tile--flipped' : ''}`}
          aria-hidden="true"
          data-aos="fade-up" data-aos-delay={i * 80}
        >
          <div className="logo-tile__face logo-tile__front">
            <img src={tile.image} alt="" loading="lazy" />
          </div>
          <div className="logo-tile__face logo-tile__back">
            <img src={tile.image} alt="" loading="lazy" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────
export default function ClientCorner() {
  const location = useLocation()

  useEffect(() => {
    AOS.refresh()

    if (location.hash) {
      const id = location.hash.slice(1)
      const timer = setTimeout(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 150)
      return () => clearTimeout(timer)
    }

    window.scrollTo(0, 0)
  }, [location.hash])

  // ── Testimonial slider state ──────────────────────────────────────
  //const sectionRef = useRef(null)
  const [sectionRef, donorInView] = useInView({ threshold: 0.2 })
  const exitTimerRef   = useRef(null)

  //const [donorInView,       setDonorInView      ] = useState(false)
  const [donorPaused,       setDonorPaused      ] = useState(false)
  const [testimonialIndex,  setTestimonialIndex ] = useState(0)
  const [donorDir,          setDonorDir         ] = useState(1)
  const [exitingDonor,      setExitingDonor     ] = useState(null)

  const changeDonor = (newIndex, dir) => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setDonorDir(dir)
    if (!reduced) {
      setExitingDonor({ donor: TESTIMONIALS[testimonialIndex], dir })
      clearTimeout(exitTimerRef.current)
      exitTimerRef.current = setTimeout(() => setExitingDonor(null), 550)
    }
    setTestimonialIndex(newIndex)
  }

  useEffect(() => () => clearTimeout(exitTimerRef.current), [])

  useEffect(() => {
    if (!donorInView || donorPaused) return
    const id = setInterval(() => {
      const next = (testimonialIndex + 1) % TESTIMONIALS.length
      changeDonor(next, 1)
    }, TESTIMONIALS_AUTOPLAY_DELAY)
    return () => clearInterval(id)
  }, [donorInView, donorPaused, testimonialIndex])

  const goToDonor = dir => {
    const next = (testimonialIndex + dir + TESTIMONIALS.length) % TESTIMONIALS.length
    changeDonor(next, dir)
  }

  // Touch swipe support
  const touchStartRef = useRef({ x: 0, y: 0 })
  const handleDonorTouchStart = e => {
    const t = e.touches[0]
    touchStartRef.current = { x: t.clientX, y: t.clientY }
  }
  const handleDonorTouchEnd = e => {
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStartRef.current.x
    const dy = t.clientY - touchStartRef.current.y
    const SWIPE_THRESHOLD = 40
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      goToDonor(dx < 0 ? 1 : -1)
    }
  }

  const currentTestimonial = TESTIMONIALS[testimonialIndex]

  return (
    <>
    {/* <MetaInfo
        title="Our Clients | CruxBytes"
        description="Trusted by startups, enterprises and government bodies. See the clients CruxBytes works with and read what they say about working with us."
        keywords="CruxBytes clients, client testimonials, client reviews, software development clients, IT company testimonials"
      /> */}
       <MetaInfo
     title={label}
      description={metaDesc}
      keywords=""
      />
    <main id="main-content" className="page-wrapper">

      {/* ── Hero ── */}
      <div className="page-hero page-hero-small">
        <div className="bg-grid bg-grid--fill" aria-hidden="true" />
        <div className="container">
          {/* <div className="section-label">- Client Corner -</div> */}
          <Breadcrumb />
          <h1 data-aos="fade-up" data-aos-delay={100}>
            {/* Our Clients */}
            {label}
            </h1>
          <p className="page-subtitle" data-aos="fade-up" data-aos-delay={200}>
            {/* Trusted by startups, enterprises, and government bodies — here's
            what our clients have to say about working with us. */}
            {pageDesc}
          </p>
          
          <div className="inner-page-actions">
            <Button href={ROUTES.SALES_ENQUIRY} variant="primary" icon="fa-arrow-right" iconPos="right" data-aos="fade-up" data-aos-delay={300}>
              JOIN OUR GROWTH STORY
            </Button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      {/* 2 ── Testimonials */}
          <section
                    id="testimonials"
                    className="cc-section  section-py cc-section--testimonials"
                    aria-labelledby="cc-tm-heading"
                    ref={sectionRef}
                    data-aos="fade-up"
                    >
            <h2 id="cc-tm-heading"  style={{textAlign:'center'}}>
              Testimonials
            </h2>
            <p className="section-subheading">
              Real words from the people we've worked with — no polished
              marketing copy, just honest feedback.
            </p>

            <div
              className="testimonial-slider"
              onMouseEnter={() => setDonorPaused(true)}
              onMouseLeave={() => setDonorPaused(false)}
            >
              <i className="fa-solid fa-quote-left testimonial-quote-icon" aria-hidden="true" />

              <button
                type="button"
                className="testimonial-slider-arrow testimonial-slider-arrow--prev"
                onClick={() => goToDonor(-1)}
                aria-label="Previous testimonial"
              >
                <i className="fa-solid fa-chevron-left" aria-hidden="true" />
              </button>

              <div
                className="testimonial-slider-viewport"
                onTouchStart={handleDonorTouchStart}
                onTouchEnd={handleDonorTouchEnd}
              >
                {exitingDonor && (
                  <div
                    key={`exit-${exitingDonor.donor.name}`}
                    className="testimonial-slider-body testimonial-slider-body--exit"
                    style={{ '--dir': exitingDonor.dir }}
                    aria-hidden="true"
                  >
                    <p className="testimonial-quote">{exitingDonor.donor.quote}</p>
                    <div className="testimonial-header">
                      <img
                        src={exitingDonor.donor.photo}
                        alt=""
                        className="testimonial-avatar-img"
                      />
                      <div className="div-testimonial-name">
                        <span className="testimonial-name">{exitingDonor.donor.name}</span>
                        <span className="testimonial-role">{exitingDonor.donor.role}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div
                  key={testimonialIndex}
                  className="testimonial-slider-body"
                  style={{ '--dir': donorDir }}
                  role="region"
                  aria-live="polite"
                >
                  <p className="testimonial-quote">{currentTestimonial.quote}</p>
                  <div className="testimonial-header">
                    <img
                      src={currentTestimonial.photo}
                      alt={currentTestimonial.name}
                      className="testimonial-avatar-img"
                    />
                    <div className="div-testimonial-name">
                      <span className="testimonial-name">{currentTestimonial.name}</span>
                      <span className="testimonial-role">{currentTestimonial.role}</span>
                    </div>
                  </div>
                  <i className="fa-solid fa-quote-right testimonial-quote-icon" aria-hidden="true" />
                </div>
              </div>

              <button
                type="button"
                className="testimonial-slider-arrow testimonial-slider-arrow--next"
                onClick={() => goToDonor(1)}
                aria-label="Next testimonial"
              >
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </button>

              <div
                className="testimonial-slider-dots"
                role="tablist"
                aria-label="Testimonial dots"
              >
                {TESTIMONIALS.map((t, i) => (
                  <button
                    key={t.name}
                    type="button"
                    role="tab"
                    className={`testimonial-slider-dot${i === testimonialIndex ? ' testimonial-slider-dot--active' : ''}`}
                    onClick={() => changeDonor(i, i > testimonialIndex ? 1 : -1)}
                    aria-label={`Go to testimonial ${i + 1}`}
                    aria-selected={i === testimonialIndex}
                  />
                ))}
              </div>
            </div>
          </section>
    

          {/* 1 ── Client Gallery */}
          <section
            id="client-gallery"
            className="container section-py cc-section"
            aria-labelledby="cc-gallery-heading"
            data-aos="fade-up"
          >
            <h2 id="cc-gallery-heading" style={{textAlign:'center'}} data-aos="fade-up" >
              Our Clients
            </h2>
            <p className="section-subheading" data-aos="fade-up" data-aos-delay={100}>
              We're proud to work alongside companies and institutions that are
              making a difference in their industries.
            </p>
            <LogoGrid />
          </section>

     <TransformBanner />

    </main>
    </>
  )
}